/**
 * ITER234 — P&L Code + Type Registry (frontend shared constant).
 *
 * Mirrors `/app/backend/services/pnl_code_registry.py` byte-for-byte
 * so the MTD tab and Review/Compare tab can never drift from the
 * backend classifier. The backend serves the same data at
 * GET /api/admin/pnl/code-registry — that endpoint exists for
 * external clients / inspection; the frontend prefers this shared
 * constant to avoid an async fetch race during initial render.
 *
 * Public helpers:
 *   • resolveLegacyName(name)  → code   (alias bridge)
 *   • resolveCodeOrName(token) → code
 *   • getEntry(code)
 *   • isCalc / isInput / isVariable / isFixed / isExpense
 *   • isOperationalCostKey(name)  — legacy callsite shim
 *   • isSkipKey(name)             — replaces the old SKIP_KEYS set
 */

const VARIABLE = 'VARIABLE';
const FIXED    = 'FIXED';
const CALC     = 'CALC';

export const PNL_CODES = [
  // Revenue
  { code: 'R-FOOD',     label: 'Food Net Revenue',                 section: 'Revenue',  type: 'REVENUE',  behaviour: VARIABLE, system_field: 'food_sale',    order: 10 },
  { code: 'R-BEV',      label: 'Beverage Net Revenue',             section: 'Revenue',  type: 'REVENUE',  behaviour: VARIABLE, system_field: 'beverage_sale',order: 11 },
  { code: 'R-ROOM',     label: 'Room Net Revenue',                 section: 'Revenue',  type: 'REVENUE',  behaviour: VARIABLE, system_field: 'room_revenue', order: 12 },
  { code: 'S-NETFB',    label: 'Net F&B Sale',                     section: 'Revenue',  type: 'SUBTOTAL', behaviour: CALC,     system_field: null, order: 20, calc_from: ['R-FOOD', 'R-BEV'] },
  { code: 'S-GROSSREV', label: 'Gross Revenue',                    section: 'Revenue',  type: 'SUBTOTAL', behaviour: CALC,     system_field: null, order: 21, calc_from: ['S-NETFB', 'R-ROOM'] },
  // COGS
  { code: 'C-FOOD',     label: 'Food Cost',                        section: 'COGS',     type: 'COGS',     behaviour: VARIABLE, system_field: 'food_cost',    order: 30 },
  { code: 'C-BEV',      label: 'Beverage Cost',                    section: 'COGS',     type: 'COGS',     behaviour: VARIABLE, system_field: 'beverage_cost',order: 31 },
  { code: 'S-FBCOST',   label: 'Total F&B Cost',                   section: 'COGS',     type: 'SUBTOTAL', behaviour: CALC,     system_field: null, order: 40, calc_from: ['C-FOOD', 'C-BEV'] },
  { code: 'S-GP',       label: 'Gross Profit',                     section: 'COGS',     type: 'SUBTOTAL', behaviour: CALC,     system_field: null, order: 41, calc_from: ['S-GROSSREV', '-S-FBCOST'] },
  // Salary & Manpower
  { code: 'E-SAL',      label: 'Salary & Wages',                   section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 50 },
  { code: 'E-INC',      label: 'Incentive / Stipend / Casual / OT',section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 51 },
  { code: 'E-PF',       label: 'PF / ESIC / Statutory',            section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 52 },
  { code: 'E-BONUS',    label: 'Bonus',                            section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 53 },
  { code: 'E-WELF',     label: 'Staff Welfare / Mediclaim / Insurance', section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 54 },
  { code: 'E-STAFFOOD', label: 'Staff Food & Accommodation',       section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 55 },
  { code: 'E-SEC',      label: 'Security Services',                section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 56 },
  { code: 'E-UNIFORM',  label: 'Uniform & Shoes',                  section: 'Salary & Manpower', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 57 },
  { code: 'S-SWB',      label: 'Salary & Manpower Cost',           section: 'Salary & Manpower', type: 'SUBTOTAL', behaviour: CALC, system_field: null, order: 60 },
  // Operating
  { code: 'E-ELEC',     label: 'Electricity',                      section: 'Operating', type: 'EXPENSE', behaviour: VARIABLE, system_field: 'electricity_cost', order: 70 },
  { code: 'E-GAS',      label: 'Cooking Fuel / Gas',               section: 'Operating', type: 'EXPENSE', behaviour: VARIABLE, system_field: 'gas_cost',     order: 71 },
  { code: 'E-FUEL',     label: 'Other Fuel / Coal',                section: 'Operating', type: 'EXPENSE', behaviour: FIXED,    system_field: null,           order: 72 },
  { code: 'E-WATER',    label: 'Water Charges',                    section: 'Operating', type: 'EXPENSE', behaviour: VARIABLE, system_field: 'water_cost',   order: 73 },
  { code: 'E-HK',       label: 'HK Materials / Supplies / Disposables',section: 'Operating', type: 'EXPENSE', behaviour: FIXED, system_field: null,         order: 74 },
  { code: 'E-LINEN',    label: 'Linen / Glassware / Crockery / Cutlery', section: 'Operating', type: 'EXPENSE', behaviour: FIXED, system_field: null,      order: 75 },
  { code: 'E-CLEAN',    label: 'Cleaning / Pest Control',          section: 'Operating', type: 'EXPENSE', behaviour: FIXED,    system_field: null,           order: 76 },
  { code: 'E-REPAIR',   label: 'Repairs & Maintenance',            section: 'Operating', type: 'EXPENSE', behaviour: VARIABLE, system_field: 'repairs_cost', order: 77 },
  { code: 'E-IT',       label: 'IT / Software AMC / Internet',     section: 'Operating', type: 'EXPENSE', behaviour: FIXED,    system_field: null,           order: 78 },
  { code: 'E-PACK',     label: 'Packing Material',                 section: 'Operating', type: 'EXPENSE', behaviour: FIXED,    system_field: null,           order: 79 },
  { code: 'S-OPEX',     label: 'Operating Cost',                   section: 'Operating', type: 'SUBTOTAL', behaviour: CALC,    system_field: null,           order: 80 },
  // Admin & Selling
  { code: 'E-LEGAL',    label: 'Legal & Professional Fees',        section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 90 },
  { code: 'E-EXCISE',   label: 'Excise / License / Consultation',  section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 91 },
  { code: 'E-BANK',     label: 'Bank Charges',                     section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 92 },
  { code: 'E-PRINT',    label: 'Printing & Stationery',            section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 93 },
  { code: 'E-PHONE',    label: 'Telephone & Mobile',               section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 94 },
  { code: 'E-TRAVEL',   label: 'Travelling & Conveyance',          section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 95 },
  { code: 'E-INS',      label: 'Insurance',                        section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 96 },
  { code: 'E-MISC',     label: 'Miscellaneous / Office Expense',   section: 'Admin & Selling', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 97 },
  { code: 'S-ADMIN',    label: 'Admin & Selling Cost',             section: 'Admin & Selling', type: 'SUBTOTAL', behaviour: CALC, system_field: null, order: 100 },
  // Marketing & Commissions
  { code: 'E-MKT',      label: 'Marketing & Advertising',          section: 'Marketing & Commissions', type: 'EXPENSE', behaviour: VARIABLE, system_field: 'marketing_spend_mtd', order: 110 },
  { code: 'E-COMM',     label: 'Online Commission (Zomato/Swiggy)',section: 'Marketing & Commissions', type: 'EXPENSE', behaviour: FIXED,    system_field: null,                  order: 111 },
  { code: 'S-MKT',      label: 'Marketing & Commissions Cost',     section: 'Marketing & Commissions', type: 'SUBTOTAL', behaviour: CALC, system_field: null, order: 120 },
  // Fixed
  { code: 'E-RENT',     label: 'Premises Rent',                    section: 'Fixed', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 130 },
  { code: 'E-OTHFIX',   label: 'Other Fixed Costs',                section: 'Fixed', type: 'EXPENSE', behaviour: FIXED, system_field: null, order: 131 },
  { code: 'S-FIXED',    label: 'Total Fixed Cost',                 section: 'Fixed', type: 'SUBTOTAL', behaviour: CALC, system_field: null, order: 140 },
  // Totals
  { code: 'T-TOTEXP',   label: 'Total Expenditure',                section: 'Totals', type: 'TOTAL', behaviour: CALC, system_field: null, order: 200 },
  { code: 'T-PL',       label: 'Profit & Loss',                    section: 'Totals', type: 'TOTAL', behaviour: CALC, system_field: null, order: 210 },
];

export const CODE_INDEX = Object.fromEntries(PNL_CODES.map(e => [e.code, e]));

// ── Legacy alias bridge (lowercased + space-collapsed → code) ────────
const _ALIASES_RAW = {
  'R-FOOD':    ['food net revenue', 'food sale', 'food revenue', 'food sales'],
  'R-BEV':     ['liquor & beverage net revenue', 'beverage net revenue',
                 'beverage sale', 'beverage revenue', 'bev sale',
                 'beverage sales', 'liquor sales', 'liquor & beverage'],
  'R-ROOM':    ['room net revenue', 'room revenue'],
  'C-FOOD':    ['food cost'],
  'C-BEV':     ['liquor & bev cost', 'beverage cost', 'bev cost'],
  'S-NETFB':   ['net fnb sale', 'net  fnb sale', 'net f&b sale'],
  'S-GROSSREV':['gross revenue', 'total revenue'],
  'S-FBCOST':  ['total f & b cost', 'total  f & b cost', 'total f&b cost', 'total cost of sales'],
  'S-GP':      ['gross profit'],
  'S-SWB':     ['salary & manpower cost', 'total swb cost'],
  'S-OPEX':    ['operating cost', 'total operation cost'],
  'S-ADMIN':   ['admn & selling cost', 'total admin & selling cost', 'admin & selling cost'],
  'S-MKT':     ['marketing and commissions cost', 'marketing  and commissions cost'],
  'S-FIXED':   ['total fixed cost'],
  'T-TOTEXP':  ['total expenditure', 'total expenses', 'total cost'],
  'T-PL':      ['profit & loss', 'profit/ loss', 'profit / loss', 'net profit'],
  'E-SAL':     ['salary paid out', 'salaries & wages', 'salary & wages', 'salary'],
  'E-INC':     ['stipend payment/ casual/hk service/kst', 'incentive/ stipend/ gratuity', 'overtime/ o.d.c staff'],
  'E-PF':      ['pf employers cont.', 'pf expenses', 'esic expenses'],
  'E-BONUS':   ['bonus paid out-adoc provision on basis of last year', 'bonus'],
  'E-WELF':    ['staff mediclaim expenses /public liability insurance', 'staff welfare/ medical'],
  'E-STAFFOOD':['staff food cost', 'staff room rent', 'staff food & accomodation', 'house rent/ flat maintanance'],
  'E-SEC':     ['security services (security + vallet)', 'security services'],
  'E-UNIFORM': ['uniform & shoes exp.', 'uniform & shoes'],
  'E-ELEC':    ['electricity', 'common electricity'],
  'E-GAS':     ['fuel cost  (cooking gas)', 'fuel cost (cooking gas)', 'kitchen fuel- gas'],
  'E-FUEL':    ['kitchen coal', 'other fuel'],
  'E-WATER':   ['water charges'],
  'E-HK':      ['hk materials- supplies- disposables - linen replace', 'kitchen expenses', 'restaurant expenses'],
  'E-LINEN':   ['linen', 'glassware', 'crockery / cutlery', 'utensils/ kitchenwere'],
  'E-CLEAN':   ['cleaning expenses', 'laundry expenses', 'pest control', 'pest control expenses'],
  'E-REPAIR':  ['material purchase (maintenance dept)', 'pc/ups/cctv/printer and asso. repairs',
                 'repairs & maintanance', 'repairs & maintenance', 'maintenance'],
  'E-IT':      ['software amc (winhms/greythr)', 'wi-fi services / internet expenses', 'computer expense'],
  'E-PACK':    ['packing material'],
  'E-LEGAL':   ['legal & professional fees', 'amc & legal and professional'],
  'E-EXCISE':  ['excise online consultation charges'],
  'E-BANK':    ['bank charges'],
  'E-PRINT':   ['printing & stationery', 'menu'],
  'E-PHONE':   ['telephone & mobile charges'],
  'E-TRAVEL':  ['travelling/ coneyance', 'travelling & conveyance'],
  'E-INS':     ['insuarance charges', 'insurance'],
  'E-MISC':    ['miscellaneous/ office expense', 'banquet expenses/ party/ festival', 'flower & decoration'],
  'E-MKT':     ['marketing', 'advertisement/ sales marketing', 'marketing & advertising'],
  'E-COMM':    ['zomato/swiggy/mmt online commission', 'commision zomato/ swiggy/ send me'],
  'E-RENT':    ['premises rent', 'rent'],
  'E-OTHFIX':  ['othe fixed costs', 'other fixed costs', 'other_fixed'],
};

const _norm = (s) => (s || '').toLowerCase().split(/\s+/).filter(Boolean).join(' ');

export const ALIAS_MAP = (() => {
  const m = new Map();
  for (const [code, names] of Object.entries(_ALIASES_RAW)) {
    for (const n of names) m.set(_norm(n), code);
  }
  // Also let canonical labels resolve to their own code
  for (const e of PNL_CODES) m.set(_norm(e.label), e.code);
  return m;
})();


// ── Public helpers ───────────────────────────────────────────────────
export function resolveLegacyName(name) {
  return ALIAS_MAP.get(_norm(name)) || null;
}

export function resolveCodeOrName(token) {
  if (!token) return null;
  const upper = String(token).trim().toUpperCase();
  if (CODE_INDEX[upper]) return upper;
  return resolveLegacyName(token);
}

export function getEntry(code) {
  return CODE_INDEX[(code || '').toUpperCase()] || null;
}

export function isCalc(code) {
  const e = getEntry(code);
  return !!e && (e.type === 'SUBTOTAL' || e.type === 'TOTAL');
}
export function isInput(code) {
  const e = getEntry(code);
  return !!e && (e.type === 'REVENUE' || e.type === 'COGS' || e.type === 'EXPENSE');
}
export function isVariable(code) {
  const e = getEntry(code);
  return !!e && e.behaviour === VARIABLE;
}
export function isFixed(code) {
  const e = getEntry(code);
  return !!e && e.behaviour === FIXED;
}
export function isExpense(code) {
  const e = getEntry(code);
  return !!e && e.type === 'EXPENSE';
}

/** Replacement for the old `isOperationalCostKey()` heuristic.
 *  Accepts a code (v2 native) or a legacy name. Returns true only
 *  when the resolved code is a VARIABLE EXPENSE (Electricity, Gas,
 *  Water, Repairs, Marketing). Unknown names → false. */
export function isOperationalCostKey(token) {
  const code = resolveCodeOrName(token);
  if (!code) return false;
  return isVariable(code) && isExpense(code);
}

/** Replacement for the old `SKIP_KEYS` set. Returns true when the
 *  resolved code is a CALC row OR a REVENUE/COGS leaf (those render
 *  via dedicated revenue/COGS sections, not the op-ex/fixed loop). */
export function isSkipKey(token) {
  const code = resolveCodeOrName(token);
  if (!code) return false;
  const e = getEntry(code);
  return e.type === 'SUBTOTAL' || e.type === 'TOTAL'
      || e.type === 'REVENUE'  || e.type === 'COGS';
}
